import { useCallback, useEffect, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import CreatableSelect from 'react-select/creatable'

import { codeItemAdd, codeListGet, tagManagerPersist } from '../../../api'
import { AttachmentView } from '../../../common/attachments/attachmentview'
import { Button } from '../../../common/Button.tsx'
import DefaultDatePicker from '../../../common/datepicker/DefaultDatePicker.jsx'
import { PersonSelect } from '../../../common/personselect'
import { creatableSelectCodeSettings, creatableSelectTagSettings } from '../../../common/reactselect'
import Switch from '../../../common/Switch'
import { fetchTags } from '../../../common/utils'
import { actionTypeSelector, authSelector } from '../../../reducers'

// code lists
const educationLevelCodes = 'competenceEducationLevel'
const degreeCodes = 'competenceDegrees'

export function AddEducation({ show, setShow, educationData, saveData }) {
	const { t } = useTranslation()
	const actionType = useSelector(actionTypeSelector)
	const auth = useSelector(authSelector)
	const prefix = 'educationModal'
	const path = window.location.pathname
	const isPersonal = path.startsWith('/personal')

	// react-select
	const handleCodeItem = (newValue, actionMeta) =>
		setData({ ...data, [actionMeta.name]: { codeItemId: newValue.value } })

	const [educationLevelOptions, setEducationLevelOptions] = useState([])

	const fetchEducationLevels = useCallback(async () => {
		const res = await codeListGet(educationLevelCodes)
		setEducationLevelOptions(
			res.data.items
				? res.data.items
						.map((item) => ({
							value: item.codeItemId, // item.key
							label: item.codeValue, // item.description
						}))
						.sort((a, b) => a.label.localeCompare(b.label))
				: [],
		)
	}, [])

	const handleCreateEducationLevel = async (value) => {
		const res = await codeItemAdd(educationLevelCodes, { codeValue: value })
		const { codeItemId, codeValue } = res.data
		setEducationLevelOptions([...educationLevelOptions, { value: codeItemId, label: codeValue }])
		setData({
			...data,
			educationLevelCodeItem: { codeItemId: codeItemId },
		})
	}

	const [degreeOptions, setDegreeOptions] = useState([])

	const fetchDegrees = useCallback(async () => {
		const res = await codeListGet(degreeCodes)
		setDegreeOptions(
			res.data.items
				? res.data.items
						.map((item) => ({
							value: item.codeItemId, // item.key
							label: item.codeValue, // item.description
						}))
						.sort((a, b) => a.label.localeCompare(b.label))
				: [],
		)
	}, [])

	const handleCreateDegree = async (value) => {
		const res = await codeItemAdd(degreeCodes, { codeValue: value })
		const { codeItemId, codeValue } = res.data
		setDegreeOptions([...degreeOptions, { value: codeItemId, label: codeValue }])
		setData({
			...data,
			degreeCodeItem: { codeItemId: codeItemId },
		})
	}

	const [tagOptions, setTagOptions] = useState([])

	const handleTags = (options) =>
		setData({
			...data,
			tags: options.map((option) => ({ codeItemId: option.value, codeKey: option.label })),
		})

	const handleCreateTag = async (value) => {
		const res = await tagManagerPersist([{ codeKey: value }])
		const { codeItemId, codeKey } = res.data[0]
		setTagOptions([...tagOptions, { value: codeItemId, label: codeKey }])
		const currentTags = data.tags ?? []
		setData({
			...data,
			tags: [...currentTags, { codeItemId: codeItemId, codeKey: codeKey }],
		})
	}

	// fetch code lists
	useEffect(() => {
		fetchEducationLevels()
		fetchDegrees()
		fetchTags(setTagOptions)
	}, [fetchEducationLevels, fetchDegrees])

	const [data, setData] = useState(null)

	useEffect(() => setData(educationData), [educationData])

	const dataChange = ({ target }) => {
		const newData = { ...data }
		const value = target.type === 'checkbox' ? !target.checked : target.value // reversed checkbox for finished field

		if (target.id === 'graduationYear') {
			newData.graduationDate = null
		} else if (target.id === 'finished') {
			if (value === false) {
				newData.graduationEstimateYear = data.graduationYear
				newData.graduationYear = null
				newData.graduationDate = null
			} else {
				newData.graduationYear = data.graduationEstimateYear
				newData.graduationEstimateYear = null
			}
		}

		newData[target.id] = value
		setData(newData)
	}

	const handleGraduationDateChange = (iso) => {
		const newData = { ...data }
		newData.graduationDate = iso ?? null
		if (iso) {
			newData.graduationYear = new Date(iso).getFullYear()
		}
		setData(newData)
	}

	// react-select
	const selectChange = (newValue, actionMeta) => setData({ ...data, [actionMeta.name]: newValue.value })

	const handleSave = () => saveData(data)
	const handleClose = () => setShow(false)

	let title
	if (data?.educationId > 0) {
		title = data?.degreeCodeItem?.codeValue ?? t('Degree', 'Tutkinto')
	} else {
		title = t('AddEducation', 'Lisää tutkinto')
	}

	return (
		show &&
		data && (
			<Modal show onHide={handleClose} backdrop="static" size="xl" fullscreen="lg-down" scrollable>
				<Modal.Header closeButton>
					<Modal.Title>{title}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Row className="mb-1">
						<PersonSelect
							disabled={data?.educationId > 0 || isPersonal}
							selectedPerson={data}
							selectChange={selectChange}
							supervised={auth.role === 'SUPERVISOR'}
						/>
					</Row>
					<Row className="mb-1">
						<Form.Group as={Col} controlId="educationLevelCodeItem">
							<Form.Label>{t('EducationLevel', 'Koulutustaso')}</Form.Label>
							<CreatableSelect
								name="educationLevelCodeItem"
								inputId="educationLevelCodeItem"
								value={educationLevelOptions.find(
									(option) => option.value === data?.educationLevelCodeItem?.codeItemId,
								)}
								onChange={handleCodeItem}
								onCreateOption={handleCreateEducationLevel}
								isValidNewOption={(inputValue) => inputValue.length > 0 && auth.knowledgeAdmin}
								options={educationLevelOptions}
								{...creatableSelectCodeSettings(t)}
							/>
						</Form.Group>
					</Row>
					<Row className="mb-1">
						<Form.Group as={Col} controlId="degreeCodeItem">
							<Form.Label>{t('EducationDegree', 'Tutkinto')}</Form.Label>
							<CreatableSelect
								name="degreeCodeItem"
								inputId="degreeCodeItem"
								value={degreeOptions.find((option) => option.value === data?.degreeCodeItem?.codeItemId)}
								onChange={handleCodeItem}
								onCreateOption={handleCreateDegree}
								isValidNewOption={(inputValue) => inputValue.length > 0 && auth.knowledgeAdmin}
								options={degreeOptions}
								{...creatableSelectCodeSettings(t)}
							/>
						</Form.Group>
					</Row>
					<Row className="mb-1">
						<Form.Group as={Col} controlId="fieldOfOrientation">
							<Form.Label>{t('FieldOfOrientation', 'Suuntautumisala/pääaine')}</Form.Label>
							<Form.Control required onChange={dataChange} value={data?.fieldOfOrientation ?? ''} />
						</Form.Group>
					</Row>
					<Row className="mb-1">
						<Form.Group as={Col} controlId="school">
							<Form.Label>{t('Academy', 'Oppilaitos')}</Form.Label>
							<Form.Control required onChange={dataChange} value={data?.school ?? ''} />
						</Form.Group>
					</Row>
					<Row className="mb-1">
						<Form.Group as={Col} className="mt-4" xl={3} controlId="graduationDate">
							{data?.finished && (
								<DefaultDatePicker
									id={`${prefix}-graduationDate`}
									name="graduationDate"
									label={t('DateOfExamination', 'Valmistumispäivä')}
									value={data?.graduationDate ?? ''}
									onChange={handleGraduationDateChange}
								/>
							)}
						</Form.Group>
						<Form.Group as={Col} xl={3} controlId="graduationYear">
							{data?.finished && (
								<>
									<Form.Label>{t('GraduationYear', 'Valmistumisvuosi')}</Form.Label>
									<Form.Control type="number" min="1920" onChange={dataChange} value={data?.graduationYear ?? ''} />
								</>
							)}
						</Form.Group>
						<Form.Group as={Col} xl={3} style={{ marginTop: '2rem' }} controlId="finished">
							<Switch
								className="mb-1"
								id="finished"
								label={t('EducationNotFinished', 'Opiskelu kesken')}
								onChange={dataChange}
								checked={!data?.finished}
							/>
						</Form.Group>
						{!data?.finished && (
							<Form.Group as={Col} xl={3} controlId="graduationEstimateYear">
								<Form.Label>{t('GraduationEstimateYear', 'Arvioitu valmistumisvuosi')}</Form.Label>
								<Form.Control
									type="number"
									min="1920"
									onChange={dataChange}
									value={data?.graduationEstimateYear ?? ''}
								/>
							</Form.Group>
						)}
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="educationDescription">
							<Form.Label>{t('Description', 'Kuvaus')}</Form.Label>
							<Form.Control
								as="textarea"
								required
								placeholder={t('Description', 'Kuvaus')}
								rows={3}
								onChange={dataChange}
								value={data?.educationDescription ?? ''}
							/>
						</Form.Group>
					</Row>

					<Row className="mb-3">
						<Form.Group as={Col} controlId="educationkeywords">
							<Form.Label>{t('Keywords', 'Avainsanat')}</Form.Label>
							<CreatableSelect
								inputId="educationkeywords"
								value={data?.tags?.map((tag) => ({ value: tag.codeItemId, label: tag.codeKey }))}
								onChange={handleTags}
								onCreateOption={handleCreateTag}
								options={tagOptions}
								{...creatableSelectTagSettings(t)}
							/>
						</Form.Group>
					</Row>

					<h4>{t('Attachments', 'Liitteet')}</h4>
					<AttachmentView
						section="education"
						entityid={data?.educationId}
						attachments={data?.attachments}
						savedId={
							(actionType === 'POST_EDUCATION_SUCCEEDED' || actionType === 'POST_EDUCATION_EDIT_SUCCEEDED') &&
							data?.educationId
						}
					/>
				</Modal.Body>
				<Modal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
					<Button variant="cancel" onClick={handleClose}>
						{t('Cancel', 'Keskeytä')}
					</Button>
					<Button variant="primary" onClick={handleSave} disabled={!data?.personId}>
						{t('Save', 'Tallenna')}
					</Button>
				</Modal.Footer>
			</Modal>
		)
	)
}
